import clsx from 'clsx'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineArrowsRightDownIcon from 'LuxKit/Icons/line/LineArrowsRightDownIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import type SvgIcon from 'LuxKit/SvgIcon'
import { LABEL_HEIGHT } from 'LuxKit/Label/constants'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import getPropertyIcon from 'lib/offer/getPropertyIcon'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import { getMaxNonInfantGuestCount } from 'lib/offer/occupancyUtils'

import AccommodationAmenitiesAndPoliciesModal from './AccommodationAmenitiesAndPoliciesModal'
import { mapRoomPackage } from './roomPackageUtils'

const FlexGroup = styled(Group)`
  flex: 1;
`

const WrappableLabel = styled(StructureLabel)`
  white-space: normal;
  height: unset;
  min-height: ${LABEL_HEIGHT};
  padding-block: ${rem(2)};
`

const AmenitiesAndPoliciesCtaContainer = styled.div`
  &.start-on-new-line {
    flex-basis: 100%;
  }
`

interface Amenity {
  key: string
  name: string
  Icon: typeof SvgIcon
}

interface RoomPackageDetails {
  labelVariant: 'light' | 'information'
  size?: string
  capacity?: number
  beds?: string
  bedrooms?: number
  wifi?: string
  parking?: string
  amenities?: Array<Amenity>
}

function extractPropertyRoomPackageDetailList({ capacities, bedrooms, bedGroupDescriptions, size, popularAmenities }: App.Property.Room, orWord: string): RoomPackageDetails {
  // NB: If `roomPackage.capacities` is empty, we _don't_ want to display `"Sleeps 0"`.
  const totalCapacity = getMaxNonInfantGuestCount(capacities)

  const amenities: Array<Amenity> = []
  if (size) {
    // NB: The key's an arbitrary UUIDv4 that's effectively guaranteed not to clash with any room
    // amenity codes returned by accommodation service.
    amenities.push({ key: '2cb95570-46b7-4c18-95b3-5090173facad', name: size, Icon: LineArrowsRightDownIcon })
  }
  for (const { id, name, iconName } of popularAmenities) {
    amenities.push({ key: id, name, Icon: getPropertyIcon(iconName) })
  }

  return {
    labelVariant: 'information',
    // NB: We display room size as the first "amenity" faux label, not as a "detail" label.
    capacity: totalCapacity || undefined,
    beds: bedGroupDescriptions.join(` ${orWord} `) || undefined,
    bedrooms,
    amenities,
  }
}

function extractHotelRoomPackageDetailList(
  roomPackage: App.HotelPackage,
  orWord: string,
  showAmenities: boolean,
): RoomPackageDetails {
  const dimension = roomPackage.roomType.sizeSqm
  const beds = (roomPackage.roomType.bedGroups ?? []).map((b) => b.description).join(` ${orWord} `)
  const bedrooms = roomPackage.roomType.numberOfBedrooms
  const amenities: Array<Amenity> = []

  if (showAmenities && dimension) {
    amenities.push({ key: `${roomPackage.roomType.id}-size`, name: `${dimension}m²`, Icon: LineArrowsRightDownIcon })
  }

  if (showAmenities) {
    for (const amenity of roomPackage.roomType.amenities) {
      amenities.push({
        key: `${amenity.group}-${amenity.id}`,
        name: amenity.description,
        Icon: getOfferFeatureIcon(amenity.symbol),
      })
    }
  }

  return {
    labelVariant: 'light',
    size: showAmenities ? undefined : dimension ? `${dimension}m²` : undefined,
    beds: beds || undefined,
    bedrooms,
    amenities: amenities.length ? amenities : undefined,
  }
}

function extractBedbankRoomPackageDetailList(roomPackage: App.BedbankPackage): RoomPackageDetails {
  const features = new Map(roomPackage.features.map((f) => ([f.id, f.text])))

  // TODO: Where does roomPackage.bedGroupsDescription go?

  return {
    labelVariant: 'light',
    beds: features.get('bed'),
    capacity: getMaxNonInfantGuestCount(roomPackage.capacities.combinations),
    size: features.get('dimension'),
    wifi: features.get('wifi'),
    parking: features.get('parking'),
  }
}

interface Props {
  roomPackage: App.Property.Room | App.HotelPackage | App.BedbankPackage
  urgencyLabels?: ReactNode
  /**
   * Hides max non-infant occupancy and bedroom count labels.
   *
   * Urgency, size, bed configuration, and amenity labels are unaffected.
   */
  hideCounts?: boolean
  shouldShowAmenitiesAndPoliciesCta?: boolean
  showHotelRoomAmenities?: boolean
  showAmenitiesAndPoliciesCtaOnNewLine?: boolean
}

function AccommodationRoomDetailsList(props: Props) {
  const {
    roomPackage,
    urgencyLabels,
    hideCounts,
    shouldShowAmenitiesAndPoliciesCta = true,
    showHotelRoomAmenities = false,
    showAmenitiesAndPoliciesCtaOnNewLine = false,
  } = props

  const intl = useIntl()

  const showModal = useContext(ModalContext)

  const orWord = intl.formatMessage({
    id: 'GzXJi62SQuqpWHf7Jmwj',
    defaultMessage: 'OR',
    description: 'Separator between alternative bed group options (e.g. "1 King Bed OR 2 Single Beds")',
  })

  const detailsList = useMemo(() => {
    return mapRoomPackage(
      roomPackage,
      (room) => extractPropertyRoomPackageDetailList(room, orWord),
      (pkg) => extractHotelRoomPackageDetailList(pkg, orWord, showHotelRoomAmenities),
      extractBedbankRoomPackageDetailList,
    )
  }, [roomPackage, orWord, showHotelRoomAmenities])
  const openAmenitiesAndPoliciesModal = useCallback(() => {
    showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={roomPackage} hideCounts={hideCounts} />)
  }, [roomPackage, showModal, hideCounts])

  const viewAmenitiesAndPoliciesText = intl.formatMessage({
    id: 'RqhmmXe05aLenwFTiitr',
    defaultMessage: 'View amenities & policies',
    description: 'CTA text that opens the room amenities & policies modal.',
  })

  const bedroomsLabel = (bedroomCount: number) => {
    return intl.formatMessage(
      {
        id: 'TymFMOQjKzT063HM0cSP',
        defaultMessage: '{count, plural, one {# bedroom} other {# bedrooms}}',
        description: 'Label showing the number of bedrooms for a room/package.',
      },
      { count: bedroomCount },
    )
  }

  const sleepsLabel = (guestCount: number) => {
    return intl.formatMessage(
      {
        id: 'rHuohmpw67VoLBruN_2o',
        defaultMessage: 'Sleeps {count}',
        description: 'Label indicating the maximum guest capacity for a room/package.',
      },
      { count: guestCount },
    )
  }

  return <FlexGroup direction="vertical" horizontalAlign="start" gap={12} noDisplayWhenEmpty>
    <LabelGroup>
      {urgencyLabels}
      {detailsList.size && <StructureLabel variant={detailsList.labelVariant} startIcon={<LineArrowsRightDownIcon aria-hidden="true" />}>
        {detailsList.size}
      </StructureLabel>}
      {!hideCounts && detailsList.capacity && <StructureLabel variant={detailsList.labelVariant} startIcon={<LineUsersAltIcon aria-hidden="true" />}>
        {sleepsLabel(detailsList.capacity)}
      </StructureLabel>}
      {detailsList.beds && <WrappableLabel variant={detailsList.labelVariant} startIcon={<LineBedIcon aria-hidden="true" />}>
        {detailsList.beds}
      </WrappableLabel>}
      {!hideCounts && detailsList.bedrooms && <WrappableLabel variant={detailsList.labelVariant} startIcon={<LineBedIcon aria-hidden="true" />}>
        {bedroomsLabel(detailsList.bedrooms)}
      </WrappableLabel>}
    </LabelGroup>
    {!!detailsList.amenities && <Group direction="horizontal" gap="8 16" wrap="wrap">
      {detailsList.amenities.map(({ key, name, Icon }) => <BodyTextBlock key={key} variant="medium" startIcon={<Icon aria-hidden="true" />}>
        {name}
      </BodyTextBlock>)}
      {shouldShowAmenitiesAndPoliciesCta && <AmenitiesAndPoliciesCtaContainer
        className={clsx({ 'start-on-new-line': showAmenitiesAndPoliciesCtaOnNewLine })}
        data-testid="accommodation-room-details-cta-container"
      >
        <Clickable onClick={openAmenitiesAndPoliciesModal}>
          <BodyTextBlock variant="medium" endIcon={<LineAngleRightIcon aria-hidden="true" />} underline>{viewAmenitiesAndPoliciesText}</BodyTextBlock>
        </Clickable>
      </AmenitiesAndPoliciesCtaContainer>}
    </Group>}
  </FlexGroup>
}

export default AccommodationRoomDetailsList
